import { PlanType } from "@omnia/shared/generated/enums"
import { getPlanActivity } from "../../activity"
import { defineComponent, handleApiError, IComponentContext, IOmniaShell } from "../../omnia"
import { INutritionPlan, INutritionService, IPlanEditArguments } from "../../types"
import { getPlanPillText } from "../../utils"
import { CroppingEditVm } from "./cropping/croppingEditVm"
import { PlanEditContext } from "./editContext"
import { ProductApplicationVm } from "./products/productApplicationVm"
import { PlanProductsEditCtrl } from "./products/products"
import { IEditPlanCroppingArguments } from "./types"
import { askCommitOptions, validateCommit } from "./validation"
import { showPlanUpdateValidationDialog } from "@omnia/shared/plans/validation/validation"
import { Dialog } from "@omnia/shared/shell/dialog"
import { getDistinctValues } from "@omnia/shared/utils/array"
import { getCropColor } from "@omnia/shared/fields"
import { colorToCss, getWhiteBlackForegroundFromBackgroundColor } from "@omnia/shared/color/color"
import { showDeleteCancel } from "@omnia/shared/shell/msgbox/msgbox"


export class PlanEditCtrl extends Dialog {

    static create(): ng.IDirective {

        return defineComponent(PlanEditCtrl, "app/nutrition/plan/edit/edit-dialog.html")

    }

    private ctxt: PlanEditContext

    public croppingEditContext: IComponentContext

    public productsEditCtrl: PlanProductsEditCtrl
    public productsEditContext: IComponentContext

    public editBiomass: boolean

    static $inject = ["$scope", "OmniaNutritionService", "OmniaShell"]

    constructor(
        $scope: ng.IScope,
        public svc: INutritionService,
        shell: IOmniaShell,
        readonly readOnly = shell.isReadOnly(p => p.nutritionPlans)
    ) {

        super($scope, shell)

    }

    $onInit() {
        this.setup()
    }

    protected setup() {

        this.svc.ensureInit().then(() => {

            let args: IPlanEditArguments = this.arguments

            const readOnly = this.shell.isReadOnly(p => p.nutritionPlans) || args.readOnly

            this.ctxt = new PlanEditContext(this.svc, args, readOnly)

            this.createProdutsEditContext()
            this.createCroppingEditContext()

            this._icon = "fas fa-pen"

            if (this.ctxt.newPlan) {
                this._title = "Create a new nutrition plan"
            }
            else {
                this._title = "Update a nutrition plan"
            }

            this.editBiomass = args.defaultToBiomass || this.ctxt.useBiomass

            this.setActivity(getPlanActivity(this.newPlan, this.target))

        })

    }

    get target(): INutritionPlan {
        return this.ctxt ? this.ctxt.target : null
    }

    private createCroppingEditContext() {

        var args: IEditPlanCroppingArguments = {
            context: this.ctxt,
            cropping: null
        }

        this.croppingEditContext = <any>{
            isActive: () => true,
            execArguments: args,
        }

    }

    private createProdutsEditContext() {

        this.productsEditContext = <any>{
            isActive: () => true,
            execArguments: this.ctxt
        }

    }

    get newPlan() {
        return this.ctxt && this.ctxt.newPlan
    }

    get existingPlan() {
        return this.ctxt && (!this.ctxt.newPlan)
    }

    get mergeAppZones() {
        return this.target?.MergeAppZones
    }

    set mergeAppZones(value) {
        this.target.MergeAppZones = value
    }

    get canMergeAppZones() {
        return !(this.ctxt?.useBiomass)
    }

    get showCroppingSelector() {
        return this.ctxt && this.ctxt.showCroppingSelector
    }

    getPillText() {
        if (!this.target || !this.target.Name) return ""
        return getPlanPillText(this.target.Name)
    }

    getPillStyle(): any {
        if (!this.ctxt) return {}

        var c: number[]

        var crops = this.ctxt.cropping.filter(c => c.selected && !c.remove).map(c => c.crop)
        crops = getDistinctValues(crops, c => c, c => c.CropId)
        var singleCrop = (crops.length === 1 ? crops[0] : null)

        c = singleCrop ? getCropColor(singleCrop) : [66, 66, 66]

        var f = getWhiteBlackForegroundFromBackgroundColor(c)

        return {
            "background-color": colorToCss(c, false),
            "color": f ? "#fff" : "#000"
        }

    }

    getSelectedCropping(): CroppingEditVm[] {
        return this.ctxt.getSelectedCropping()
    }

    editCropping(cropping?: CroppingEditVm) {

        var args: IEditPlanCroppingArguments = {
            context: this.ctxt,
            cropping: cropping
        }

        this.ctxt.showCroppingSelector = true

        this.croppingEditContext = <any>{
            isActive: () => this.componentContext.isActive(),
            execArguments: args,
        }

    }

    getPlanProducts(): ProductApplicationVm[] {
        return this.ctxt.applications
    }


    addNewProduct() {

        var component = <PlanProductsEditCtrl>this.productsEditContext.component
        component.addNewProduct()

    }

    editProduct(p: ProductApplicationVm) {

        var component = <PlanProductsEditCtrl>this.productsEditContext.component
        component.editProduct(p)

    }

    deletePlan() {

        if (this.newPlan) return
        if (this.readOnly) return

        var content = "Do you really want to delete the current plan?<br><br>This will also delete all associated data (requirements, recommendations, and map layers)"
        var title = `Delete plan '${this.target.Name}'`

        var r = showDeleteCancel(this.shell, content, title)

        r.then(ok => {

            if (ok) {
                this.svc.deletePlan(this.target.NutritionPlanId)
                    .then(() => this.closeComponent(true))
                    .catch(e => handleApiError(this.shell, e))
            }

        })

    }

    canCommit(): boolean {

        return (this.form && this.form.$valid && this.ctxt.hasSelectedCrops() && this.ctxt.isValid() && (!this.readOnly))

    }

    commit() {

        if (!this.canCommit()) {
            this.showValidationErrors = true
            return
        }

        this.showValidationErrors = false

        return validateCommit(this.ctxt, this.shell)
            .then(ok => ok
                ? this.validatePlanUpdate()
                : ok
            )
            .then(ok => {

                if (ok) {

                    return askCommitOptions(this.ctxt, this.shell).then(model => {

                        if (model) {

                            return this.svc.savePlan(model)
                                .then(id => {
                                    this.close(id)
                                    return id
                                })
                                .catch(r => handleApiError(this.shell, r, { resourceType: "plan" }))

                        }


                    })

                }

            })

    }

    doCancel() {
        this.shell.disableMenu = false
        this.cancel()
    }

    validatePlanUpdate() {

        const { newPlan, ctxt } = this

        if (newPlan) {
            return true
        }

        const plan = ctxt.getUpdatedTargetModel(true)

        const request = {
            year: plan.YearId,
            planType: PlanType.NutritionPlan,
            plans: [
                {
                    key: String(plan.NutritionPlanId),
                    name: plan.Name,
                    cropping: (plan.Cropping ?? []).map(c => ({
                        croppingId: c.CroppingId
                    }))
                }
            ]
        }

        return showPlanUpdateValidationDialog(this.svc.planValidationService, request)

    }
}
